import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Users, Mail, Newspaper, TrendingUp, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { adminApi } from "@/lib/adminApi";
import Counter from "@/components/Counter";

const cards = [
    { key: "donations_count", label: "Donations", icon: Heart, color: "text-caireen-red bg-caireen-red/10" },
    { key: "memberships", label: "Memberships", icon: Users, color: "text-caireen-orange bg-caireen-orange/10" },
    { key: "contacts", label: "Contact messages", icon: Mail, color: "text-caireen-blue bg-caireen-blue/10" },
    { key: "newsletter", label: "Subscribers", icon: Newspaper, color: "text-caireen-olive bg-caireen-olive/10" },
];

function formatEur(n) {
    try {
        return new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
    } catch {
        return `€${Math.round(n || 0)}`;
    }
}

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi
            .overview()
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-32 text-caireen-ink/40">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }
    if (!data) return <p className="text-caireen-ink/60">Failed to load data.</p>;

    return (
        <div data-testid="admin-dashboard" className="space-y-10">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="font-sans text-xs uppercase tracking-[0.25em] text-caireen-orange">Admin · Overview</p>
                    <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight">
                        Hallo, willkommen zurück.
                    </h1>
                    <p className="mt-2 font-sans text-caireen-ink/60">
                        Echtzeit-Übersicht aller Einreichungen und Spenden.
                    </p>
                </div>
                <div className="bg-white rounded-2xl ring-1 ring-caireen-ink/10 px-6 py-4 flex items-center gap-4">
                    <TrendingUp className="w-6 h-6 text-caireen-orange" />
                    <div>
                        <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Total raised</p>
                        <p data-testid="admin-total-donations" className="font-serif text-3xl">
                            {formatEur(data.donations_total)}
                        </p>
                    </div>
                </div>
            </header>

            {/* Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {cards.map((c, i) => (
                    <motion.div
                        key={c.key}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                        whileHover={{ y: -4 }}
                        className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 p-6 hover:shadow-[0_24px_60px_-24px_rgba(31,41,55,0.18)] transition-all"
                        data-testid={`admin-card-${c.key}`}
                    >
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${c.color}`}>
                            <c.icon className="w-5 h-5" strokeWidth={1.75} />
                        </div>
                        <p className="mt-5 font-sans text-xs uppercase tracking-widest text-caireen-ink/55">{c.label}</p>
                        <Counter to={data[c.key] || 0} className="block font-serif text-5xl text-caireen-ink leading-none mt-1.5" />
                        {c.key === "memberships" && (
                            <p className="mt-2 font-sans text-xs text-caireen-orange">
                                {data.memberships_pending} pending
                            </p>
                        )}
                    </motion.div>
                ))}
            </div>

            {/* Monthly donations chart */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 p-6 md:p-8"
                data-testid="admin-monthly-chart"
            >
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Last 6 months</p>
                        <h2 className="font-serif text-2xl md:text-3xl tracking-tight">Donations</h2>
                    </div>
                </div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.donations_monthly} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D6" vertical={false} />
                            <XAxis dataKey="month" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v}`} />
                            <Tooltip
                                cursor={{ fill: "rgba(232,133,51,0.08)" }}
                                contentStyle={{
                                    background: "#FDFBF7",
                                    border: "1px solid rgba(31,41,55,0.1)",
                                    borderRadius: 12,
                                    fontFamily: "Manrope",
                                }}
                                formatter={(v) => [formatEur(v), "Total"]}
                            />
                            <Bar dataKey="total" radius={[10, 10, 0, 0]} fill="#E88533" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </motion.div>
        </div>
    );
}
